import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRoute,
  useRouterState,
} from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import { getCookie } from '@tanstack/react-start/server'
import { watchConnection } from '@wagmi/core'
import { cx } from 'cva'
import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { Toaster } from 'sonner'
import { WagmiProvider } from 'wagmi'
import Check from '~icons/lucide/check'
import CircleAlert from '~icons/lucide/circle-alert'

import * as Auth from '#/lib/auth.js'
import { wagmiConfig } from '#/lib/config.js'
import { Hotkeys } from '#/lib/hotkeys.js'
import { persister, queryClient } from '#/lib/query-client.js'
import * as Datadog from '#/lib/telemetry/datadog.js'
import * as PostHog from '#/lib/telemetry/posthog.js'
import * as SentryBrowser from '#/lib/telemetry/sentry-browser.js'
import * as Theme from '#/lib/theme.js'
import appCss from '#/styles.css?url'
import { TempoLogo, TempoMark } from '#/ui/TempoLogo.js'

const getTheme = createServerFn({ method: 'GET' }).handler(() =>
  Theme.parse(getCookie(Theme.cookie)),
)

export const Route = createRootRoute({
  beforeLoad: async () => ({
    theme: await getTheme(),
  }),
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1, maximum-scale=1' },
      { title: 'Tempo Wallet' },
      {
        content: 'Your wallet for Tempo balances, transfers, deposits, and account control.',
        name: 'description',
      },
      { content: 'Tempo Wallet', property: 'og:title' },
      {
        content: 'Your wallet for Tempo balances, transfers, deposits, and account control.',
        property: 'og:description',
      },
      { content: 'https://wallet.tempo.xyz/og-image.png', property: 'og:image' },
      { content: '1200', property: 'og:image:width' },
      { content: '630', property: 'og:image:height' },
      { content: 'summary_large_image', name: 'twitter:card' },
      { content: 'https://wallet.tempo.xyz/og-image.png', name: 'twitter:image' },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      {
        href: '/favicon-light.svg',
        media: '(prefers-color-scheme: light)',
        rel: 'icon',
        type: 'image/svg+xml',
      },
      {
        href: '/favicon-dark.svg',
        media: '(prefers-color-scheme: dark)',
        rel: 'icon',
        type: 'image/svg+xml',
      },
    ],
  }),
  component: RootComponent,
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: ReactNode }) {
  const { theme } = Route.useRouteContext()
  const remote = useRouterState({
    select: (state) => {
      const search = state.location.search as { mode?: unknown }
      // Overlay surfaces (the approval screens + the `/post-message` handshake
      // page) own their own chrome; the root only decides whether the document
      // is the app (opaque, watermarked) or an embedded overlay (transparent).
      const overlay =
        state.location.pathname.startsWith('/remote') || state.location.pathname === '/post-message'
      const embedded =
        overlay &&
        (search.mode === 'iframe' || (typeof window !== 'undefined' && window !== window.parent))
      return { embedded, overlay }
    },
  })
  return (
    <html
      data-regen-radius="none"
      lang="en"
      // Embedded overlays paint their own dimmed scrim, so the document itself
      // must be transparent from the first render (any opaque default would
      // flash before route-level effects could clear it).
      style={
        remote.embedded
          ? { ...Theme.style(theme), backgroundColor: 'transparent' }
          : Theme.style(theme)
      }
    >
      <head>
        <HeadContent />
      </head>
      <body className={cx('text-foreground', !remote.embedded && 'bg-background')}>
        {!remote.overlay && (
          // App watermark — only on the wallet's own pages, never on the
          // embedded overlay surfaces (which paint their own chrome).
          <div
            aria-hidden
            className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-background"
          >
            <TempoMark className="absolute top-1/2 left-1/2 h-auto w-[min(320px,48vw)] -translate-x-1/2 -translate-y-1/2 text-foreground opacity-[0.035]" />
          </div>
        )}
        {/* The watermark layer above is `fixed` and paints a background, so
            route content needs its own stacking context to render over it. */}
        <div className="relative z-10">{children}</div>
        <Scripts />
      </body>
    </html>
  )
}

// Sync query state across the parent window and every same-origin
// iframe over a `BroadcastChannel`. Skipped in cross-origin iframes
// (e.g. the wallet embed) where the channel can't be shared and
// non-cloneable wagmi connector state would throw `DataCloneError`.
if (typeof window !== 'undefined' && 'BroadcastChannel' in window && isSameOrigin()) {
  const original_postMessage = BroadcastChannel.prototype.postMessage
  BroadcastChannel.prototype.postMessage = function (msg) {
    try {
      return original_postMessage.call(this, msg)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'DataCloneError') return
      throw err
    }
  }
  broadcastQueryClient({ queryClient, broadcastChannel: 'tempo-wallet-query' })
}

if (typeof window !== 'undefined')
  watchConnection(wagmiConfig, {
    onChange(connection, previousConnection) {
      const address = Auth.getSessionAddress(connection)
      const previousAddress = Auth.getSessionAddress(previousConnection)
      if (!previousAddress || previousAddress === address) return
      void Auth.session.clearState(queryClient, previousAddress)
    },
  })

function RootComponent() {
  useEffect(() => {
    void Datadog.init()
    void PostHog.init()
    void SentryBrowser.init()
  }, [])

  return (
    <WagmiProvider config={wagmiConfig}>
      <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
        <ComplianceGate>
          <IdentifyUser />
          <Hotkeys />
          <Outlet />
        </ComplianceGate>
        <Toaster
          icons={{
            error: <CircleAlert className="size-4" />,
            success: <Check className="size-4" />,
          }}
          position="bottom-center"
          theme="system"
          toastOptions={{
            style: {
              background: 'var(--background-color-surface)',
              border: '1px solid var(--border-color-border)',
              borderRadius: 'var(--radius-body)',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.04), 0 2px 4px -2px rgb(0 0 0 / 0.04)',
              color: 'var(--text-color-foreground)',
              fontSize: 'var(--text-label-14)',
              fontWeight: '400',
              lineHeight: 'var(--text-label-14--line-height)',
            },
          }}
        />
      </PersistQueryClientProvider>
    </WagmiProvider>
  )
}

/** Renders children normally, but swaps to a blocked screen if the API returns 451 (compliance-flagged). */
function ComplianceGate(props: { children: ReactNode }) {
  const address = Auth.useSessionAddress()
  const me = Auth.me.useQuery()
  if (!address) return props.children
  if (me.error?.message === 'COMPLIANCE_BLOCKED') return <Blocked />
  return props.children
}

function IdentifyUser() {
  const address = Auth.useSessionAddress()
  useEffect(() => {
    if (!address) {
      Datadog.reset()
      PostHog.reset()
      return
    }

    void Datadog.identify(address)
    void PostHog.active(address)
  }, [address])
  return null
}

function Blocked() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface text-foreground">
      <div className="fixed top-6 left-6">
        <TempoLogo className="h-[18px] w-auto" />
      </div>
      <main className="flex w-[min(480px,calc(100%-48px))] flex-col items-center gap-2 text-center">
        <h1 className="text-heading-24">Account Unavailable</h1>
        <p className="text-label-15 text-foreground-secondary">
          This account has been restricted and is no longer accessible. If you believe this is an
          error, please contact support.
        </p>
      </main>
    </div>
  )
}

function isSameOrigin() {
  if (window === window.top) return true
  try {
    // Accessing `parent.location.href` throws on cross-origin iframes.
    void window.parent.location.href
    return true
  } catch {
    return false
  }
}
